import * as React from 'react';
import './style.scss';
import { OptionChainData, OptionsPrediction } from '../../reducers/optionsSlice';
import { StrikeHighlight } from '../../utils/optionsService';

const formatPeriodDate = (dateStr: string) => {
    const date = new Date(`${dateStr}T12:00:00`);
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatPeriodRange = (startDate?: string, endDate?: string) => {
    if (!startDate || !endDate) {
        return '';
    }

    return `${formatPeriodDate(startDate)} – ${formatPeriodDate(endDate)}`;
};

interface OptionsChainChartProps {
    chainData: OptionChainData | null;
    currentPrice: number;
    title?: string;
    expirationDates?: string[];
    selectedExpirationDate?: string;
    onExpirationDateChange?: (expirationDate: string) => void;
    strikeHighlights?: Record<number, StrikeHighlight>;
    prediction?: OptionsPrediction | null;
    endPrice?: number;
    priceChangePercent?: number;
    periodStartDate?: string;
    periodEndDate?: string;
    mode?: 'past' | 'forward';
    loading?: boolean;
}

export const OptionsChainChart: React.FC<OptionsChainChartProps> = ({
    chainData,
    currentPrice,
    title = 'Options Chain Analysis',
    expirationDates: availableExpirationDates,
    selectedExpirationDate = 'all',
    onExpirationDateChange,
    strikeHighlights = {},
    prediction = null,
    endPrice,
    priceChangePercent,
    periodStartDate,
    periodEndDate,
    mode = 'forward',
    loading = false,
}) => {
    const [showAllStrikes, setShowAllStrikes] = React.useState(false);

    if (loading) {
        return (
            <div className="options-chain-chart">
                <div className="chart-header">
                    <h3>{title}</h3>
                </div>
                <div className="chart-empty">
                    <p>Loading options chain data...</p>
                </div>
            </div>
        );
    }

    if (!chainData || (!chainData.calls.length && !chainData.puts.length)) {
        return (
            <div className="options-chain-chart">
                <div className="chart-header">
                    <h3>{title}</h3>
                </div>
                <div className="chart-empty">
                    <p>No options data available</p>
                </div>
            </div>
        );
    }

    const callVolume = chainData.calls.reduce((sum, call) => sum + (call.volume || 0), 0);
    const putVolume = chainData.puts.reduce((sum, put) => sum + (put.volume || 0), 0);
    const putCallRatio = putVolume / (callVolume || 1);
    const expirationDates = availableExpirationDates || chainData.expiration_dates || (chainData.expiration_date ? [chainData.expiration_date] : []);
    const selectedLabel = selectedExpirationDate === 'all' ? `All dates (${expirationDates.length})` : selectedExpirationDate;
    const callByStrike = new Map(chainData.calls.map(call => [call.strike, call.volume || 0]));
    const putByStrike = new Map(chainData.puts.map(put => [put.strike, put.volume || 0]));
    const strikes = Array.from(new Set([
        ...chainData.calls.map(call => call.strike),
        ...chainData.puts.map(put => put.strike)
    ]));
    const allVolumeRows = strikes
        .map(strike => ({
            strike,
            callVolume: callByStrike.get(strike) || 0,
            putVolume: putByStrike.get(strike) || 0,
            highlight: strikeHighlights[strike],
        }))
        .map(row => ({
            ...row,
            totalVolume: row.callVolume + row.putVolume,
            distanceFromPrice: Math.abs(row.strike - currentPrice),
        }))
        .filter(row => row.totalVolume > 0)
        .sort((first, second) => second.totalVolume - first.totalVolume);
    const volumeRows = [...(showAllStrikes ? allVolumeRows : allVolumeRows.slice(0, 15))]
        .sort((first, second) => first.strike - second.strike);
    const maxVolume = Math.max(...volumeRows.flatMap(row => [row.callVolume, row.putVolume]), 1);
    const nearestStrike = volumeRows.length
        ? volumeRows.reduce((nearest, row) => (
            row.distanceFromPrice < nearest.distanceFromPrice ? row : nearest
        ), volumeRows[0])
        : null;

    const highlightedCallWins = allVolumeRows.filter((row) => row.highlight?.call === 'success').length;
    const highlightedPutWins = allVolumeRows.filter((row) => row.highlight?.put === 'success').length;
    const highlightedCallPredicted = mode === 'forward'
        ? allVolumeRows.filter((row) => row.highlight?.callPredicted).length
        : 0;
    const highlightedPutPredicted = mode === 'forward'
        ? allVolumeRows.filter((row) => row.highlight?.putPredicted).length
        : 0;

    const getBarWidth = (volume: number) => {
        if (!volume) return '0%';
        return `${Math.max((volume / maxVolume) * 100, 4)}%`;
    };

    const getHighlightClass = (side: 'call' | 'put', highlight?: StrikeHighlight) => {
        if (!highlight) {
            return '';
        }

        if (mode === 'past') {
            const outcome = side === 'call' ? highlight.call : highlight.put;
            return outcome === 'success' ? 'highlight-success' : '';
        }

        const predicted = side === 'call' ? highlight.callPredicted : highlight.putPredicted;
        return predicted ? 'highlight-predicted' : '';
    };

    return (
        <div className={`options-chain-chart ${mode}`}>
            <div className="chart-header">
                <div className="chart-title-group">
                    <h3>{title}</h3>
                    {prediction && (
                        <div className="strategy-summary">
                            <span className={`prediction-badge ${prediction.prediction}`}>
                                {prediction.prediction.toUpperCase()}
                            </span>
                            {mode === 'forward' && (
                                <span className="prediction-confidence">{prediction.confidence}% confidence</span>
                            )}
                        </div>
                    )}
                </div>
                <div className="chart-info">
                    {onExpirationDateChange && expirationDates.length > 0 ? (
                        <label className="expiration-selector">
                            <span>{mode === 'past' ? 'Expiry' : 'Expiration'}</span>
                            <select value={selectedExpirationDate} onChange={(event) => onExpirationDateChange(event.target.value)}>
                                {mode === 'forward' && <option value="all">All next 3 months</option>}
                                {expirationDates.map(date => (
                                    <option key={date} value={date}>{date}</option>
                                ))}
                            </select>
                        </label>
                    ) : mode === 'past' ? (
                        <span className="expiration">Past 3 months · per expiry volume</span>
                    ) : (
                        <span className="expiration">{selectedLabel}</span>
                    )}
                    {periodStartDate && periodEndDate && (
                        <span className="period-range">{formatPeriodRange(periodStartDate, periodEndDate)}</span>
                    )}
                    <span className="current-price">
                        {mode === 'past' ? 'Start' : 'Current'}: ${currentPrice.toFixed(2)}
                    </span>
                    {mode === 'past' && endPrice !== undefined && (
                        <span className="current-price end-price">Expiry: ${endPrice.toFixed(2)}</span>
                    )}
                    {mode === 'past' && priceChangePercent !== undefined && (
                        <span className={`price-change ${priceChangePercent >= 0 ? 'positive' : 'negative'}`}>
                            {priceChangePercent >= 0 ? '+' : ''}{priceChangePercent.toFixed(2)}%
                        </span>
                    )}
                </div>
            </div>

            <div className="chart-container">
                {mode === 'forward' && expirationDates.length > 0 && periodStartDate && (
                    <div className="expiration-dates">
                        {expirationDates.map(date => (
                            <span key={date}>{formatPeriodRange(periodStartDate, date)}</span>
                        ))}
                    </div>
                )}

                {mode === 'past' && (
                    <div className="highlight-summary">
                        <span className="summary-item success">Call wins: {highlightedCallWins}</span>
                        <span className="summary-item success">Put wins: {highlightedPutWins}</span>
                    </div>
                )}

                {mode === 'forward' && (highlightedCallPredicted > 0 || highlightedPutPredicted > 0) && (
                    <div className="highlight-summary">
                        <span className="summary-item predicted">Predicted calls: {highlightedCallPredicted}</span>
                        <span className="summary-item predicted">Predicted puts: {highlightedPutPredicted}</span>
                    </div>
                )}

                <div className="chart-controls">
                    <div className="rows-summary">
                        Showing {volumeRows.length.toLocaleString()} of {allVolumeRows.length.toLocaleString()} strike rows
                    </div>
                    {allVolumeRows.length > 15 && (
                        <button type="button" onClick={() => setShowAllStrikes(!showAllStrikes)}>
                            {showAllStrikes ? 'Show top 15' : 'Show all'}
                        </button>
                    )}
                </div>

                <div className="volume-table">
                    <div className="volume-table-header">
                        <span>Calls</span>
                        <span>Strike</span>
                        <span>Puts</span>
                    </div>

                    <div className="volume-rows">
                        {volumeRows.length > 0 ? volumeRows.map(row => (
                            <div key={row.strike} className="volume-row">
                                <div
                                    className={`volume-cell call-cell ${getHighlightClass('call', row.highlight)}`}
                                    title={`Call volume: ${row.callVolume.toLocaleString()}${mode === 'past' && row.highlight?.call === 'success' ? ' · win' : ''}${mode === 'forward' && row.highlight?.callPredicted ? ' · predicted' : ''}`}
                                >
                                    <span className="volume-number call-color">{row.callVolume.toLocaleString()}</span>
                                    <div className="volume-track">
                                        <div className="volume-fill call" style={{ width: getBarWidth(row.callVolume) }} />
                                    </div>
                                    {mode === 'forward' && row.highlight?.callPredicted && <span className="side-badge predicted">Pred</span>}
                                    {mode === 'past' && row.highlight?.call === 'success' && <span className="side-badge success">Win</span>}
                                </div>

                                <div className={`strike-cell ${nearestStrike && row.strike === nearestStrike.strike ? 'near-price' : ''}`}>
                                    <span>${row.strike.toFixed(0)}</span>
                                    {nearestStrike && row.strike === nearestStrike.strike && <small>near</small>}
                                </div>

                                <div
                                    className={`volume-cell put-cell ${getHighlightClass('put', row.highlight)}`}
                                    title={`Put volume: ${row.putVolume.toLocaleString()}${mode === 'past' && row.highlight?.put === 'success' ? ' · win' : ''}${mode === 'forward' && row.highlight?.putPredicted ? ' · predicted' : ''}`}
                                >
                                    {mode === 'forward' && row.highlight?.putPredicted && <span className="side-badge predicted">Pred</span>}
                                    {mode === 'past' && row.highlight?.put === 'success' && <span className="side-badge success">Win</span>}
                                    <div className="volume-track">
                                        <div className="volume-fill put" style={{ width: getBarWidth(row.putVolume) }} />
                                    </div>
                                    <span className="volume-number put-color">{row.putVolume.toLocaleString()}</span>
                                </div>
                            </div>
                        )) : <div className="volume-empty">No volume available for returned contracts</div>}
                    </div>
                </div>

                <div className="stats-section">
                    <div className="stat-card">
                        <div className="stat-label">Total Call Volume</div>
                        <div className="stat-value call-color">
                            {callVolume.toLocaleString()}
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">Total Put Volume</div>
                        <div className="stat-value put-color">
                            {putVolume.toLocaleString()}
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">Put/Call Ratio</div>
                        <div className="stat-value">
                            {putCallRatio.toFixed(2)}x
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">Displayed Strikes</div>
                        <div className="stat-value">
                            {volumeRows.length.toLocaleString()}/{allVolumeRows.length.toLocaleString()}
                        </div>
                    </div>
                </div>
            </div>

            <div className="chart-legend">
                <div className="legend-item">
                    <span className="legend-color put"></span>
                    <span className="legend-label">Put Options (bearish bets)</span>
                </div>
                <div className="legend-item">
                    <span className="legend-color call"></span>
                    <span className="legend-label">Call Options (bullish bets)</span>
                </div>
                {mode === 'past' && (
                    <div className="legend-item">
                        <span className="legend-color success"></span>
                        <span className="legend-label">Win — in-the-money based on stock move</span>
                    </div>
                )}
                {mode === 'forward' && (
                    <div className="legend-item">
                        <span className="legend-color predicted"></span>
                        <span className="legend-label">Pred — past 3M high-volume strike + sentiment</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export {
    PastOptionsStrategyDashboard,
    NextOptionsStrategyDashboard,
    type PastStrategySummary,
    type FutureStrategySummary,
} from './options-strategy-dashboard';
